import React, { useEffect, useMemo, useState, useContext } from 'react';
import { Alert, Button, Card, Col, Divider, Form, Input, message, Modal, Progress, Row, Select, Space, Spin, Switch, Tag, Typography } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { isMobileOnly } from 'react-device-detect';
import {
    CheckCircleOutlined, CloseCircleOutlined, CloseOutlined, CopyOutlined,
    DeleteOutlined, ExclamationCircleOutlined, LockOutlined, MinusCircleOutlined,
    RocketOutlined,
} from '@ant-design/icons';
import MessagingSetupWizard from './MessagingSetupWizard';
import { getOrganizationsApi } from '../../api/apiInstances';
import logger from '../../utils/logger';
import AccessControl from '../common/AccessControl';
import FormButtons from '../common/FormButtons';
import { accessibilityRoles } from '../../utils/accessUtils';
import { AlertContext } from '../../context/AlertContext';
import { ALERT_DURATIONS } from '../../constants/alerts';
import { DentobookApplicationContext } from '../../context/DentobookApplicationContext';
import { useHelpSection } from '../../context/HelpSectionContext';

const { Title, Text, Paragraph } = Typography;

const CHANNEL_OPTIONS = [
    { value: 'none', labelId: 'channel_none' },
    { value: 'email', labelId: 'channel_email' },
    { value: 'sms', labelId: 'channel_sms' },
    { value: 'whatsapp', labelId: 'channel_whatsapp' },
];

type TwilioStatus = 'not_configured' | 'configured_not_verified' | 'verified' | 'verification_failed';

function deriveTwilioStatus(configured: boolean, verifiedAt: string | null | undefined, verifyError: string | null): TwilioStatus {
    if (!configured) return 'not_configured';
    if (verifyError) return 'verification_failed';
    if (verifiedAt) return 'verified';
    return 'configured_not_verified';
}

const TWILIO_CONSOLE_URL = 'https://www.twilio.com/console';
const TWILIO_SMS_DOCS_URL = 'https://www.twilio.com/docs/phone-numbers';
const TWILIO_WHATSAPP_DOCS_URL = 'https://www.twilio.com/docs/whatsapp';

interface MessagingSettingsProps {
    setIsEditMode: (v: boolean) => void;
}

const MessagingSettings: React.FC<MessagingSettingsProps> = ({ setIsEditMode: setOrgEditMode }) => {
    const intl = useIntl();
    const { userOrganization } = useContext(DentobookApplicationContext);
    const { setSuccessMessage, setVisible, setError } = useContext(AlertContext);
    const organizationsApi = getOrganizationsApi();
    useHelpSection('messaging-settings');

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [verifyError, setVerifyError] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [showWizard, setShowWizard] = useState(false);
    const [wizardDismissed, setWizardDismissed] = useState(false);

    // Saved state — mirrors last successful load/save; used for Cancel revert
    const [savedChannel, setSavedChannel] = useState('none');
    const [savedOffsets, setSavedOffsets] = useState<number[]>([1440, 120]);
    const [savedQHStart, setSavedQHStart] = useState('');
    const [savedQHEnd, setSavedQHEnd] = useState('');
    const [savedSmsEnabled, setSavedSmsEnabled] = useState(false);
    const [savedWhatsappEnabled, setSavedWhatsappEnabled] = useState(false);
    const [savedTwilioConfigured, setSavedTwilioConfigured] = useState(false);
    const [savedTwilioVerifiedAt, setSavedTwilioVerifiedAt] = useState<string | null>(null);
    const [savedTwilioSidLast4, setSavedTwilioSidLast4] = useState<string | null>(null);
    const [savedTwilioSmsFrom, setSavedTwilioSmsFrom] = useState('');
    const [savedTwilioWhatsappFrom, setSavedTwilioWhatsappFrom] = useState('');

    // Form field state — editable copies
    const [defaultChannel, setDefaultChannel] = useState('none');
    const [reminderOffsets, setReminderOffsets] = useState<number[]>([1440, 120]);
    const [quietHoursStart, setQuietHoursStart] = useState('');
    const [quietHoursEnd, setQuietHoursEnd] = useState('');
    const [smsEnabled, setSmsEnabled] = useState(false);
    const [whatsappEnabled, setWhatsappEnabled] = useState(false);
    const [twilioAccountSid, setTwilioAccountSid] = useState('');
    const [twilioAuthToken, setTwilioAuthToken] = useState('');
    const [twilioSmsFrom, setTwilioSmsFrom] = useState('');
    const [twilioWhatsappFrom, setTwilioWhatsappFrom] = useState('');
    const [clearingCredentials, setClearingCredentials] = useState(false);
    const [isClearModalOpen, setIsClearModalOpen] = useState(false);

    const twilioStatus = deriveTwilioStatus(savedTwilioConfigured, savedTwilioVerifiedAt, verifyError);
    const twilioVerified = twilioStatus === 'verified';

    // ── Sandbox detection: Twilio WhatsApp sandbox number ──
    const TWILIO_SANDBOX_NUMBER = '+14155238886';
    const isWhatsappSandbox = savedTwilioWhatsappFrom === TWILIO_SANDBOX_NUMBER;

    // ── Per-channel readiness ──
    type ChannelStatus = 'ready' | 'not_enabled' | 'missing_sender' | 'not_configured' | 'not_verified' | 'sandbox_only';

    const smsStatus: ChannelStatus = useMemo(() => {
        if (!savedTwilioConfigured) return 'not_configured';
        if (!twilioVerified) return 'not_verified';
        if (!savedSmsEnabled) return 'not_enabled';
        if (!savedTwilioSmsFrom) return 'missing_sender';
        return 'ready';
    }, [savedTwilioConfigured, twilioVerified, savedSmsEnabled, savedTwilioSmsFrom]);

    const whatsappStatus: ChannelStatus = useMemo(() => {
        if (!savedTwilioConfigured) return 'not_configured';
        if (!twilioVerified) return 'not_verified';
        if (!savedWhatsappEnabled) return 'not_enabled';
        if (!savedTwilioWhatsappFrom) return 'missing_sender';
        if (isWhatsappSandbox) return 'sandbox_only';
        return 'ready';
    }, [savedTwilioConfigured, twilioVerified, savedWhatsappEnabled, savedTwilioWhatsappFrom, isWhatsappSandbox]);

    // ── Setup completeness (5 factors, 20% each) ──
    const setupPercent = useMemo(() => {
        let score = 0;
        if (savedTwilioConfigured) score += 20;           // credentials saved
        if (twilioVerified) score += 20;                   // credentials verified
        if (savedTwilioSmsFrom) score += 20;               // SMS sender configured
        if (savedTwilioWhatsappFrom) score += 20;          // WhatsApp sender configured
        if (savedSmsEnabled || savedWhatsappEnabled) score += 20; // at least one channel enabled
        return score;
    }, [savedTwilioConfigured, twilioVerified, savedTwilioSmsFrom, savedTwilioWhatsappFrom, savedSmsEnabled, savedWhatsappEnabled]);

    // ── Next action hint ──
    const nextAction: string | null = useMemo(() => {
        if (!savedTwilioConfigured) return 'next_action_run_wizard';
        if (!twilioVerified) return 'next_action_verify';
        if (!savedSmsEnabled && !savedWhatsappEnabled) return 'next_action_enable_channel';
        if (savedSmsEnabled && !savedTwilioSmsFrom) return 'next_action_add_sms_sender';
        if (savedWhatsappEnabled && !savedTwilioWhatsappFrom) return 'next_action_add_whatsapp_sender';
        return null;
    }, [savedTwilioConfigured, twilioVerified, savedSmsEnabled, savedWhatsappEnabled, savedTwilioSmsFrom, savedTwilioWhatsappFrom]);

    const isDirty = useMemo(() =>
        defaultChannel !== savedChannel ||
        JSON.stringify([...reminderOffsets].sort((a, b) => a - b)) !==
            JSON.stringify([...savedOffsets].sort((a, b) => a - b)) ||
        quietHoursStart !== savedQHStart ||
        quietHoursEnd !== savedQHEnd ||
        smsEnabled !== savedSmsEnabled ||
        whatsappEnabled !== savedWhatsappEnabled ||
        twilioAccountSid !== '' ||
        twilioAuthToken !== '' ||
        twilioSmsFrom !== savedTwilioSmsFrom ||
        twilioWhatsappFrom !== savedTwilioWhatsappFrom,
        [defaultChannel, reminderOffsets, quietHoursStart, quietHoursEnd,
            savedChannel, savedOffsets, savedQHStart, savedQHEnd,
            smsEnabled, savedSmsEnabled, whatsappEnabled, savedWhatsappEnabled,
            twilioAccountSid, twilioAuthToken, twilioSmsFrom, savedTwilioSmsFrom,
            twilioWhatsappFrom, savedTwilioWhatsappFrom]
    );

    // Warn on browser close/refresh when editing with unsaved changes
    useEffect(() => {
        if (!isEditing || !isDirty) return;
        const handler = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isEditing, isDirty]);

    useEffect(() => {
        loadSettings();
    }, [userOrganization?.id]);

    const applySaved = (data: {
        channel: string; offsets: number[]; qhStart: string; qhEnd: string;
        sms: boolean; whatsapp: boolean; twilioConfigured: boolean;
        twilioVerifiedAt: string | null; twilioSidLast4: string | null;
        smsFrom: string; whatsappFrom: string;
    }) => {
        setSavedChannel(data.channel);
        setSavedOffsets(data.offsets);
        setSavedQHStart(data.qhStart);
        setSavedQHEnd(data.qhEnd);
        setSavedSmsEnabled(data.sms);
        setSavedWhatsappEnabled(data.whatsapp);
        setSavedTwilioConfigured(data.twilioConfigured);
        setSavedTwilioVerifiedAt(data.twilioVerifiedAt);
        setSavedTwilioSidLast4(data.twilioSidLast4);
        setSavedTwilioSmsFrom(data.smsFrom);
        setSavedTwilioWhatsappFrom(data.whatsappFrom);
        // Sync form state
        setDefaultChannel(data.channel);
        setReminderOffsets(data.offsets);
        setQuietHoursStart(data.qhStart);
        setQuietHoursEnd(data.qhEnd);
        setSmsEnabled(data.sms);
        setWhatsappEnabled(data.whatsapp);
        setTwilioSmsFrom(data.smsFrom);
        setTwilioWhatsappFrom(data.whatsappFrom);
        // Credentials are write-only — never pre-fill
        setTwilioAccountSid('');
        setTwilioAuthToken('');
        setVerifyError(null);
    };

    const loadSettings = async () => {
        if (!userOrganization?.id) return;
        setLoading(true);
        try {
            const response = await organizationsApi.getOrganizationMessagingSettings(userOrganization.id);
            const d = response.data;
            const channel = !d.emailEnabled && !d.smsEnabled && !d.whatsappEnabled
                ? 'none'
                : (d.defaultChannel ?? 'email');
            const twilioConfigured = d.twilioConfigured ?? false;
            applySaved({
                channel,
                offsets: d.reminderOffsetsMinutes ?? [1440, 120],
                qhStart: d.quietHoursStart ?? '',
                qhEnd: d.quietHoursEnd ?? '',
                sms: d.smsEnabled ?? false,
                whatsapp: d.whatsappEnabled ?? false,
                twilioConfigured,
                twilioVerifiedAt: (d as any).twilioVerifiedAt ?? null,
                twilioSidLast4: (d as any).twilioAccountSidLast4 ?? null,
                smsFrom: d.twilioSmsFrom ?? '',
                whatsappFrom: d.twilioWhatsappFrom ?? '',
            });
            // Auto-show wizard on first run when Twilio is not configured
            if (!twilioConfigured && !wizardDismissed) {
                setShowWizard(true);
            }
        } catch (error) {
            logger.error('Failed to load messaging settings:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = () => {
        setIsEditing(true);
        setOrgEditMode(true);
    };

    const handleCancel = () => {
        setDefaultChannel(savedChannel);
        setReminderOffsets([...savedOffsets]);
        setQuietHoursStart(savedQHStart);
        setQuietHoursEnd(savedQHEnd);
        setSmsEnabled(savedSmsEnabled);
        setWhatsappEnabled(savedWhatsappEnabled);
        setTwilioSmsFrom(savedTwilioSmsFrom);
        setTwilioWhatsappFrom(savedTwilioWhatsappFrom);
        setTwilioAccountSid('');
        setTwilioAuthToken('');
        setVerifyError(null);
        setIsEditing(false);
        setOrgEditMode(false);
    };

    const handleSave = async () => {
        if (!userOrganization?.id) return;
        setSaving(true);
        try {
            const payload: Record<string, any> = {
                emailEnabled: defaultChannel === 'email',
                smsEnabled,
                whatsappEnabled,
                defaultChannel,
                reminderOffsetsMinutes: reminderOffsets,
                quietHoursStart: quietHoursStart || undefined,
                quietHoursEnd: quietHoursEnd || undefined,
                twilioSmsFrom: twilioSmsFrom || undefined,
                twilioWhatsappFrom: twilioWhatsappFrom || undefined,
            };
            // Only send credentials if the user typed new values
            if (twilioAccountSid) payload.twilioAccountSid = twilioAccountSid;
            if (twilioAuthToken) payload.twilioAuthToken = twilioAuthToken;

            await organizationsApi.updateOrganizationMessagingSettings(userOrganization.id, payload);
            // Reload to get server-computed flags
            await loadSettings();
            setIsEditing(false);
            setOrgEditMode(false);
            setSuccessMessage(intl.formatMessage({ id: 'messaging_saved' }));
            setVisible(true);
            setTimeout(() => {
                setVisible(false);
                setSuccessMessage('');
            }, ALERT_DURATIONS.DEFAULT);
        } catch (error: any) {
            logger.error('Failed to save messaging settings:', error);
            setError(error.response?.data?.detail || intl.formatMessage({ id: 'messaging_save_error' }));
            setVisible(true);
            setTimeout(() => {
                setVisible(false);
                setError('');
            }, ALERT_DURATIONS.DEFAULT);
        } finally {
            setSaving(false);
        }
    };

    const handleClearCredentialsConfirm = async () => {
        if (!userOrganization?.id) return;
        setClearingCredentials(true);
        try {
            await organizationsApi.updateOrganizationMessagingSettings(userOrganization.id, {
                clearTwilioCredentials: true,
            } as any);
            await loadSettings();
            setSuccessMessage(intl.formatMessage({ id: 'messaging_saved' }));
            setVisible(true);
            setTimeout(() => { setVisible(false); setSuccessMessage(''); }, ALERT_DURATIONS.DEFAULT);
        } catch (error: any) {
            logger.error('Failed to clear Twilio credentials:', error);
            setError(error.response?.data?.detail || intl.formatMessage({ id: 'messaging_save_error' }));
            setVisible(true);
            setTimeout(() => { setVisible(false); setError(''); }, ALERT_DURATIONS.DEFAULT);
        } finally {
            setClearingCredentials(false);
            setIsClearModalOpen(false);
        }
    };

    const handleVerifyTwilio = async () => {
        if (!userOrganization?.id) return;
        setVerifying(true);
        setVerifyError(null);
        try {
            const resp = await organizationsApi.verifyTwilioCredentials(userOrganization.id);
            const data = resp.data as any;
            if (data.verified) {
                setSuccessMessage(intl.formatMessage({ id: 'twilio_verify_success' }));
                setVisible(true);
                setTimeout(() => { setVisible(false); setSuccessMessage(''); }, ALERT_DURATIONS.DEFAULT);
                // Reload to pick up twilio_verified_at
                await loadSettings();
            } else {
                setVerifyError(data.error || intl.formatMessage({ id: 'twilio_verify_failed' }));
            }
        } catch (error: any) {
            logger.error('Twilio verification failed:', error);
            setVerifyError(error.response?.data?.detail || intl.formatMessage({ id: 'twilio_verify_failed' }));
        } finally {
            setVerifying(false);
        }
    };

    const toggleReminderOffset = (minutes: number) => {
        setReminderOffsets(prev =>
            prev.includes(minutes)
                ? prev.filter(m => m !== minutes)
                : [...prev, minutes]
        );
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
            </div>
        );
    }

    // ── Wizard ────────────────────────────────────────────────────────
    if (showWizard) {
        return (
            <AccessControl accessibleFor={[accessibilityRoles.CLINIC_ADMIN]}>
                <MessagingSetupWizard
                    onComplete={() => {
                        setShowWizard(false);
                        setWizardDismissed(true);
                        loadSettings();
                    }}
                    onSkip={() => {
                        setShowWizard(false);
                        setWizardDismissed(true);
                    }}
                    onClose={() => {
                        setShowWizard(false);
                        setWizardDismissed(true);
                    }}
                />
            </AccessControl>
        );
    }

    // ── Shared: Twilio setup guidance ─────────────────────────────────
    const twilioSetupGuidance = (
        <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fafafa', borderRadius: 6, border: '1px solid #f0f0f0' }}>
            <Paragraph style={{ marginBottom: 8, fontSize: 13, color: '#555' }}>
                <FormattedMessage id="twilio_setup_guide" />
            </Paragraph>
            <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13 }}>
                <li>
                    <a href={TWILIO_CONSOLE_URL} target="_blank" rel="noopener noreferrer">
                        <FormattedMessage id="twilio_link_console" />
                    </a>
                </li>
                <li>
                    <a href={TWILIO_SMS_DOCS_URL} target="_blank" rel="noopener noreferrer">
                        <FormattedMessage id="twilio_link_sms" />
                    </a>
                </li>
                <li>
                    <a href={TWILIO_WHATSAPP_DOCS_URL} target="_blank" rel="noopener noreferrer">
                        <FormattedMessage id="twilio_link_whatsapp" />
                    </a>
                </li>
            </ul>
        </div>
    );

    // ── Shared: masked credential display ─────────────────────────────
    const maskedSid = savedTwilioConfigured
        ? `${intl.formatMessage({ id: 'twilio_sid_masked' })}${savedTwilioSidLast4 || '••••'}`
        : intl.formatMessage({ id: 'twilio_credential_not_set' });

    const maskedToken = savedTwilioConfigured
        ? '••••••••••••••••'
        : intl.formatMessage({ id: 'twilio_credential_not_set' });

    // ── Read-only view ──────────────────────────────────────────────
    if (!isEditing) {
        const channelOpt = CHANNEL_OPTIONS.find(o => o.value === savedChannel);
        const channelDisplay = channelOpt
            ? intl.formatMessage({ id: channelOpt.labelId })
            : savedChannel;
        const reminderDisplay = [
            savedOffsets.includes(1440) && intl.formatMessage({ id: 'reminder_24h' }),
            savedOffsets.includes(120) && intl.formatMessage({ id: 'reminder_2h' }),
        ].filter(Boolean).join(', ') || intl.formatMessage({ id: 'not_available' });
        const quietDisplay = (savedQHStart && savedQHEnd)
            ? `${savedQHStart} – ${savedQHEnd}`
            : intl.formatMessage({ id: 'not_available' });

        return (
            <AccessControl accessibleFor={[accessibilityRoles.CLINIC_ADMIN]}>
                <div>
                    <Title level={4}>
                        <FormattedMessage id="messaging_settings" />
                    </Title>

                    <Row gutter={24}>
                    {/* ── Left column: settings ── */}
                    <Col xs={24} lg={14} xl={12}>
                    <div style={{ maxWidth: 520 }}>
                        {/* ── Section A: Email & Reminders ── */}
                        <SectionHeader labelId="messaging_section_email" />
                        <ViewRow labelId="default_notification_channel" value={channelDisplay} />
                        <ViewRow labelId="reminder_offsets" value={reminderDisplay} />
                        <ViewRow labelId="quiet_hours" value={quietDisplay} />
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 24, marginTop: 8 }}
                            message={<><FormattedMessage id="messaging_timezone_info" />{' '}<FormattedMessage id="messaging_quiet_hours_delay" /></>}
                        />

                        {/* ── Section B: Twilio Provider (always visible) ── */}
                        <Divider style={{ margin: '16px 0' }} />
                        <SectionHeader labelId="messaging_section_twilio" />

                        <div style={{ marginBottom: 16 }}>
                            <TwilioStatusBadge status={twilioStatus} intl={intl} />
                        </div>

                        {/* Masked credential display */}
                        <div style={{ marginBottom: 16 }}>
                            <CredentialRow
                                labelId="twilio_account_sid_label"
                                maskedValue={maskedSid}
                                isSet={savedTwilioConfigured}
                            />
                            <CredentialRow
                                labelId="twilio_auth_token_label"
                                maskedValue={maskedToken}
                                isSet={savedTwilioConfigured}
                            />
                        </div>

                        {/* Verify + Clear actions */}
                        {savedTwilioConfigured && (
                            <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
                                {!twilioVerified && (
                                    <Button
                                        size="small"
                                        onClick={handleVerifyTwilio}
                                        loading={verifying}
                                    >
                                        <FormattedMessage id="twilio_verify_btn" />
                                    </Button>
                                )}
                                <Button
                                    size="small"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => setIsClearModalOpen(true)}
                                    loading={clearingCredentials}
                                >
                                    <span><FormattedMessage id="twilio_clear_credentials_btn" /></span>
                                </Button>
                            </div>
                        )}

                        {verifyError && (
                            <Alert type="error" showIcon style={{ marginBottom: 16 }} message={verifyError} />
                        )}

                        {twilioSetupGuidance}

                        {/* ── Section C: SMS ── */}
                        <Divider style={{ margin: '16px 0' }} />
                        <SectionHeader labelId="messaging_section_sms" />
                        <ViewRow
                            labelId="sms_enabled_label"
                            value={intl.formatMessage({ id: savedSmsEnabled ? 'yes_ans' : 'no_ans' })}
                        />
                        {savedSmsEnabled && savedTwilioSmsFrom && (
                            <CopyablePhoneRow labelId="twilio_sms_from_label" value={savedTwilioSmsFrom} intl={intl} />
                        )}
                        {savedSmsEnabled && !twilioVerified && (
                            <Alert type="warning" showIcon style={{ marginBottom: 16 }}
                                message={<FormattedMessage id="twilio_not_verified_channel_warning" />} />
                        )}

                        {/* ── Section D: WhatsApp ── */}
                        <Divider style={{ margin: '16px 0' }} />
                        <SectionHeader labelId="messaging_section_whatsapp" />
                        <ViewRow
                            labelId="whatsapp_enabled_label"
                            value={intl.formatMessage({ id: savedWhatsappEnabled ? 'yes_ans' : 'no_ans' })}
                        />
                        {savedWhatsappEnabled && savedTwilioWhatsappFrom && (
                            <CopyablePhoneRow
                                labelId="twilio_whatsapp_from_label"
                                value={savedTwilioWhatsappFrom}
                                intl={intl}
                                tag={isWhatsappSandbox ? { color: 'orange', labelId: 'status_sandbox_only' } : undefined}
                            />
                        )}
                        {savedWhatsappEnabled && !twilioVerified && (
                            <Alert type="warning" showIcon style={{ marginBottom: 16 }}
                                message={<FormattedMessage id="twilio_not_verified_channel_warning" />} />
                        )}
                    </div>
                    </Col>

                    {/* ── Right column: status card ── */}
                    <Col xs={24} lg={10} xl={8}>
                        <Card
                            size="small"
                            title={<Text strong style={{ fontSize: 13 }}><FormattedMessage id="messaging_section_status" /></Text>}
                            style={{ height: 'auto' }}
                            styles={{ body: { padding: '12px 16px' } }}
                        >
                            <Space direction="vertical" size={12} style={{ width: '100%' }}>
                                <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                                    <StatusItem
                                        labelId="status_provider"
                                        value="Twilio"
                                        status={twilioStatus === 'verified' ? 'success' : twilioStatus === 'not_configured' ? 'default' : 'warning'}
                                        tagLabelId={twilioStatus === 'verified' ? 'twilio_status_verified' : twilioStatus === 'not_configured' ? 'status_not_configured' : twilioStatus === 'verification_failed' ? 'twilio_status_failed' : 'status_not_verified'}
                                        intl={intl}
                                    />
                                    <StatusItem
                                        labelId="status_sms"
                                        status={smsStatus === 'ready' ? 'success' : smsStatus === 'not_enabled' || smsStatus === 'not_configured' ? 'default' : 'warning'}
                                        tagLabelId={`status_${smsStatus}`}
                                        intl={intl}
                                    />
                                    <StatusItem
                                        labelId="status_whatsapp"
                                        status={whatsappStatus === 'ready' ? 'success' : whatsappStatus === 'sandbox_only' ? 'warning' : whatsappStatus === 'not_enabled' || whatsappStatus === 'not_configured' ? 'default' : 'warning'}
                                        tagLabelId={`status_${whatsappStatus}`}
                                        intl={intl}
                                    />
                                </div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <Progress
                                        percent={setupPercent}
                                        size="small"
                                        style={{ flex: 1 }}
                                        strokeColor={setupPercent === 100 ? '#52c41a' : '#1890ff'}
                                    />
                                    <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                                        <FormattedMessage id="messaging_setup_percent" values={{ percent: setupPercent }} />
                                    </Text>
                                </div>

                                {nextAction && (
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                                        <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: 12, flexShrink: 0, marginTop: 2 }} />
                                        <Text style={{ fontSize: 12 }}>
                                            <FormattedMessage id={nextAction} />
                                        </Text>
                                    </div>
                                )}
                            </Space>
                        </Card>
                    </Col>
                    </Row>
                    <FormButtons>
                        <Button
                            type="primary"
                            size="large"
                            shape="round"
                            icon={<RocketOutlined />}
                            onClick={() => setShowWizard(true)}
                            style={{ marginRight: 12 }}
                            className={'edit-btn ' + (isMobileOnly ? 'mobile-update-btn' : 'dentobook-btn-primary')}
                        >
                            <span><FormattedMessage id="wizard_rerun_btn" /></span>
                        </Button>
                        <Button
                            onClick={handleEdit}
                            className={'edit-btn ' + (isMobileOnly ? 'mobile-update-btn' : 'dentobook-btn-primary')}
                            type="primary"
                            size="large"
                            shape="round"
                        >
                            <FormattedMessage id="edit" />
                        </Button>
                    </FormButtons>

                    <Modal
                        transitionName=""
                        className="dentobook-modal"
                        title={<FormattedMessage id="twilio_clear_credentials_btn" />}
                        open={isClearModalOpen}
                        onOk={handleClearCredentialsConfirm}
                        onCancel={() => setIsClearModalOpen(false)}
                        okText={intl.formatMessage({ id: 'delete' })}
                        cancelText={intl.formatMessage({ id: 'cancel' })}
                        okButtonProps={{
                            className: 'modal-btn',
                            danger: true,
                            type: 'primary',
                            loading: clearingCredentials,
                        }}
                        cancelButtonProps={{
                            className: 'modal-btn',
                        }}
                        closeIcon={<CloseOutlined className="icon-24px" onClick={() => setIsClearModalOpen(false)} />}
                    >
                        <p><FormattedMessage id="twilio_clear_credentials_confirm" /></p>
                    </Modal>
                </div>
            </AccessControl>
        );
    }

    // ── Edit mode ───────────────────────────────────────────────────
    return (
        <AccessControl accessibleFor={[accessibilityRoles.CLINIC_ADMIN]}>
            <div>
                <Title level={4}>
                    <FormattedMessage id="messaging_settings" />
                </Title>

                <Form layout="vertical" style={{ maxWidth: 520 }}>
                    {/* ── Section A: Email & Reminders ── */}
                    <SectionHeader labelId="messaging_section_email" />

                    <Form.Item label={<FormattedMessage id="default_notification_channel" />}>
                        <Select
                            value={defaultChannel}
                            onChange={setDefaultChannel}
                            style={{ width: 200 }}
                            options={CHANNEL_OPTIONS.map(opt => ({
                                value: opt.value,
                                label: intl.formatMessage({ id: opt.labelId }),
                            }))}
                        />
                    </Form.Item>

                    <Form.Item label={<FormattedMessage id="reminder_offsets" />}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input
                                    type="checkbox"
                                    checked={reminderOffsets.includes(1440)}
                                    onChange={() => toggleReminderOffset(1440)}
                                />
                                <FormattedMessage id="reminder_24h" />
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input
                                    type="checkbox"
                                    checked={reminderOffsets.includes(120)}
                                    onChange={() => toggleReminderOffset(120)}
                                />
                                <FormattedMessage id="reminder_2h" />
                            </label>
                        </div>
                    </Form.Item>

                    <Form.Item label={<FormattedMessage id="quiet_hours" />}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Input
                                placeholder="HH:MM"
                                value={quietHoursStart}
                                onChange={e => setQuietHoursStart(e.target.value)}
                                style={{ width: 90 }}
                                maxLength={5}
                            />
                            <span>–</span>
                            <Input
                                placeholder="HH:MM"
                                value={quietHoursEnd}
                                onChange={e => setQuietHoursEnd(e.target.value)}
                                style={{ width: 90 }}
                                maxLength={5}
                            />
                        </div>
                        <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>
                            <FormattedMessage id="quiet_hours_hint" />
                        </div>
                    </Form.Item>

                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 24 }}
                        message={<><FormattedMessage id="messaging_timezone_info" />{' '}<FormattedMessage id="messaging_quiet_hours_delay" /></>}
                    />

                    {/* ── Section B: Twilio Provider (always visible) ── */}
                    <Divider />
                    <SectionHeader labelId="messaging_section_twilio" />

                    <div style={{ marginBottom: 16 }}>
                        <TwilioStatusBadge status={twilioStatus} intl={intl} />
                    </div>

                    <Form.Item label={<FormattedMessage id="twilio_account_sid_label" />}>
                        <Input
                            value={twilioAccountSid}
                            onChange={e => setTwilioAccountSid(e.target.value)}
                            placeholder={savedTwilioConfigured
                                ? intl.formatMessage({ id: 'twilio_credentials_saved_placeholder' })
                                : 'ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'}
                        />
                        {savedTwilioConfigured && !twilioAccountSid && (
                            <div style={{ marginTop: 4, fontSize: 12, color: '#888' }}>
                                <LockOutlined style={{ marginRight: 4 }} />
                                <FormattedMessage id="twilio_credential_saved" />: {maskedSid}
                            </div>
                        )}
                    </Form.Item>

                    <Form.Item label={<FormattedMessage id="twilio_auth_token_label" />}>
                        <Input.Password
                            value={twilioAuthToken}
                            onChange={e => setTwilioAuthToken(e.target.value)}
                            placeholder={savedTwilioConfigured
                                ? intl.formatMessage({ id: 'twilio_credentials_saved_placeholder' })
                                : ''}
                        />
                        {savedTwilioConfigured && !twilioAuthToken && (
                            <div style={{ marginTop: 4, fontSize: 12, color: '#888' }}>
                                <LockOutlined style={{ marginRight: 4 }} />
                                <FormattedMessage id="twilio_credential_saved" />: ••••••••••••••••
                            </div>
                        )}
                    </Form.Item>

                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message={<FormattedMessage id="twilio_credentials_note" />}
                    />

                    {twilioSetupGuidance}

                    {/* ── Section C: SMS ── */}
                    <Divider />
                    <SectionHeader labelId="messaging_section_sms" />

                    {!twilioVerified && (
                        <Alert type="warning" showIcon style={{ marginBottom: 16 }}
                            message={<FormattedMessage id="twilio_not_verified_channel_warning" />} />
                    )}

                    <Form.Item label={<FormattedMessage id="sms_enabled_label" />}>
                        <Switch checked={smsEnabled} onChange={setSmsEnabled} disabled={!twilioVerified} />
                    </Form.Item>

                    {smsEnabled && (
                        <Form.Item label={<FormattedMessage id="twilio_sms_from_label" />}>
                            <Input
                                value={twilioSmsFrom}
                                onChange={e => setTwilioSmsFrom(e.target.value)}
                                placeholder="+1234567890"
                            />
                            <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>
                                <FormattedMessage id="twilio_from_hint" />
                            </div>
                        </Form.Item>
                    )}

                    {/* ── Section D: WhatsApp ── */}
                    <Divider />
                    <SectionHeader labelId="messaging_section_whatsapp" />

                    {!twilioVerified && (
                        <Alert type="warning" showIcon style={{ marginBottom: 16 }}
                            message={<FormattedMessage id="twilio_not_verified_channel_warning" />} />
                    )}

                    <Form.Item label={<FormattedMessage id="whatsapp_enabled_label" />}>
                        <Switch checked={whatsappEnabled} onChange={setWhatsappEnabled} disabled={!twilioVerified} />
                    </Form.Item>

                    {whatsappEnabled && (
                        <Form.Item label={<FormattedMessage id="twilio_whatsapp_from_label" />}>
                            <Input
                                value={twilioWhatsappFrom}
                                onChange={e => setTwilioWhatsappFrom(e.target.value)}
                                placeholder="+1234567890"
                            />
                            <div style={{ color: '#888', fontSize: 12, marginTop: 4 }}>
                                <FormattedMessage id="twilio_from_hint" />
                            </div>
                        </Form.Item>
                    )}
                </Form>

                <FormButtons>
                    <Button
                        className="dentobook-btn-secondary modal-btn"
                        htmlType="button"
                        onClick={handleCancel}
                    >
                        <FormattedMessage id="cancel" />
                    </Button>
                    <Button
                        className="dentobook-btn-primary modal-btn"
                        type="primary"
                        onClick={handleSave}
                        loading={saving}
                    >
                        <FormattedMessage id="save" />
                    </Button>
                </FormButtons>
            </div>
        </AccessControl>
    );
};

const TwilioStatusBadge = ({ status, intl }: { status: TwilioStatus; intl: any }) => {
    const config: Record<TwilioStatus, { color: string; labelId: string }> = {
        not_configured: { color: 'default', labelId: 'twilio_status_not_configured' },
        configured_not_verified: { color: 'orange', labelId: 'twilio_status_not_verified' },
        verified: { color: 'green', labelId: 'twilio_status_verified' },
        verification_failed: { color: 'red', labelId: 'twilio_status_failed' },
    };
    const { color, labelId } = config[status];
    return <Tag color={color}>{intl.formatMessage({ id: labelId })}</Tag>;
};

const SectionHeader = ({ labelId }: { labelId: string }) => (
    <Title level={5} style={{ marginTop: 0, marginBottom: 12 }}>
        <FormattedMessage id={labelId} />
    </Title>
);

const ViewRow = ({ labelId, value }: { labelId: string; value: string }) => (
    <div style={{ marginBottom: 16 }}>
        <div className="body-large-regular" style={{ marginBottom: 4 }}>
            <FormattedMessage id={labelId} />:
        </div>
        <div className="body-large-medium">{value}</div>
    </div>
);

const CredentialRow = ({ labelId, maskedValue, isSet }: { labelId: string; maskedValue: string; isSet: boolean }) => (
    <div style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Text style={{ fontSize: 13, color: '#666', minWidth: 140 }}>
            <FormattedMessage id={labelId} />:
        </Text>
        <Text style={{ fontSize: 13, fontFamily: 'monospace', color: isSet ? '#333' : '#999' }}>
            {isSet && <LockOutlined style={{ marginRight: 4, fontSize: 11 }} />}
            {maskedValue}
        </Text>
    </div>
);

const STATUS_TAG_COLORS: Record<string, string> = {
    success: 'green',
    warning: 'orange',
    default: 'default',
};
const STATUS_TAG_ICONS: Record<string, React.ReactNode> = {
    success: <CheckCircleOutlined />,
    warning: <ExclamationCircleOutlined />,
    default: <MinusCircleOutlined />,
};

const StatusItem = ({ labelId, value, status, tagLabelId, intl }: {
    labelId: string; value?: string; status: 'success' | 'warning' | 'default'; tagLabelId: string; intl: any;
}) => (
    <div style={{ minWidth: 100 }}>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
            <FormattedMessage id={labelId} />
        </Text>
        <Tag color={STATUS_TAG_COLORS[status]} icon={STATUS_TAG_ICONS[status]}>
            {value ?? intl.formatMessage({ id: tagLabelId })}
        </Tag>
    </div>
);

const CopyablePhoneRow = ({ labelId, value, intl, tag }: {
    labelId: string; value: string; intl: any; tag?: { color: string; labelId: string };
}) => (
    <div style={{ marginBottom: 16 }}>
        <div className="body-large-regular" style={{ marginBottom: 4 }}>
            <FormattedMessage id={labelId} />:
        </div>
        <Space size={8} align="center">
            <Text className="body-large-medium" style={{ fontFamily: 'monospace' }}>{value}</Text>
            <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                style={{ color: '#888' }}
                onClick={() => {
                    navigator.clipboard.writeText(value);
                    message.success(intl.formatMessage({ id: 'copied' }));
                }}
            />
            {tag && <Tag color={tag.color}>{intl.formatMessage({ id: tag.labelId })}</Tag>}
        </Space>
    </div>
);

export default MessagingSettings;
